import { useState, useRef, useEffect } from 'react'
import { Button, Input, Spin, Tooltip } from 'antd'
import { RobotOutlined, SendOutlined, RightOutlined, DeleteOutlined, DoubleLeftOutlined, DoubleRightOutlined } from '@ant-design/icons'
import { usePracticeAgentSendDeepDiveMessageMutation } from '../../hooks'
import { getSession } from '../../../../lib/session'
import { formatMetricValue } from '../../utils'
import type { PracticeAgentDeepDiveMessage } from '../../types'

const { TextArea } = Input

function formatChatMetricValue(value: unknown, metricName?: string | null) {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'number') return formatMetricValue(value, metricName)
  if (typeof value === 'string') return formatMetricValue(value, metricName)
  return String(value)
}

interface ChatPanelProps {
  open: boolean
  onClose: () => void
  reportBuilderId?: string
  reportBuilderGroupId?: string | null
  reportBuilderIds?: string[]
  clientId?: string
  title?: string
  onNoteCreated?: () => void
}

export function ChatPanel({
  open,
  onClose,
  reportBuilderId,
  reportBuilderGroupId,
  reportBuilderIds,
  clientId,
  title,
  onNoteCreated,
}: ChatPanelProps) {
  const resolvedClientId = clientId ?? getSession()?.currentClientId ?? ''
  const [messages, setMessages] = useState<PracticeAgentDeepDiveMessage[]>([])
  const [input, setInput] = useState('')
  const [expanded, setExpanded] = useState(false)
  const sendMutation = usePracticeAgentSendDeepDiveMessageMutation()
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, open])

  const hasContext = !!reportBuilderId || !!reportBuilderGroupId

  function handleSend() {
    const question = input.trim()
    if (!question || !hasContext || sendMutation.isPending) return

    const userMessage: PracticeAgentDeepDiveMessage = {
      messageId: `msg-${Date.now()}-u`,
      role: 'user',
      content: question,
      insertDate: new Date().toISOString(),
      insertUserId: null,
    }

    const nextMessages = [...messages, userMessage]
    setMessages(nextMessages)
    setInput('')

    sendMutation.mutate(
      {
        reportBuilderId,
        reportBuilderGroupId,
        reportBuilderIds,
        clientId: resolvedClientId,
        question,
        messages: nextMessages,
      },
      {
        onSuccess: ({ message: responseMessage }) => {
          setMessages((prev) => [...prev, responseMessage])
          // If AI used the add_note tool, notify parent to refresh notes panel
          if (responseMessage.content?.includes('Note saved:')) {
            onNoteCreated?.()
          }
        },
      },
    )
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  function handleClear() {
    setMessages([])
    setInput('')
    sendMutation.reset()
  }

  return (
    <>

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full bg-white shadow-2xl z-50 flex flex-col transition-all duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        } ${expanded ? 'w-1/2' : 'w-[420px]'}`}
      >
        {/* Expand/collapse toggle on left edge */}
        <button
          onClick={() => setExpanded((e) => !e)}
          className={`absolute -left-5 top-1/2 -translate-y-1/2 w-5 h-16 bg-white border border-gray-200 border-r-0 rounded-l-md shadow-md flex items-center justify-center text-gray-400 feature-link hover:bg-gray-50 transition-colors z-10 ${open ? '' : 'hidden'}`}
          title={expanded ? 'Collapse panel' : 'Expand panel'}
        >
          {expanded ? <DoubleRightOutlined className="text-xs" /> : <DoubleLeftOutlined className="text-xs" />}
        </button>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <RobotOutlined className="feature-icon-accent text-lg flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-semibold text-gray-800 text-sm leading-tight truncate">PracIntel Chat</p>
              {title && <p className="text-xs text-gray-400 truncate">{title}</p>}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {messages.length > 0 && (
              <Tooltip title="Clear conversation">
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={handleClear}
                  className="text-gray-400 hover:text-red-500"
                />
              </Tooltip>
            )}
            <Button
              type="text"
              size="small"
              icon={<RightOutlined />}
              onClick={onClose}
              className="text-gray-400 hover:text-gray-700"
            />
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.length === 0 && !sendMutation.isPending && (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-400 gap-3">
              <RobotOutlined className="text-4xl text-gray-300" />
              <div>
                <p className="text-sm font-medium">Ask PracIntel about this report</p>
                <p className="text-xs mt-1">e.g. "What's driving the low utilization?" or "Which team has the highest WIP?"</p>
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.messageId}
              className={`flex flex-col gap-1 ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  msg.role === 'user'
                    ? 'feature-button-solid text-white rounded-br-sm'
                    : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                }`}
              >
                {msg.content}
              </div>

              {/* Computed result card (from analyze_data tool) */}
              {msg.role === 'assistant' && msg.computedResult && (
                <div className="max-w-full w-full mt-1">
                  {msg.computedResult.rows && msg.computedResult.rows.length > 0 && msg.computedResult.columns ? (
                    <div className="overflow-x-auto rounded border border-blue-100 bg-blue-50">
                      <table className="text-xs border-collapse w-full">
                        <thead>
                          <tr className="bg-blue-100">
                            {msg.computedResult.columns.map((col) => (
                              <th key={col} className="px-2 py-1 text-left font-semibold text-blue-700 whitespace-nowrap">
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {msg.computedResult.rows.map((row, i) => (
                            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-blue-50/50'}>
                              {(msg.computedResult?.columns ?? []).map((col) => (
                                <td key={col} className="px-2 py-1 text-gray-700 whitespace-nowrap">
                                  {formatChatMetricValue(row[col], col)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : msg.computedResult.value !== undefined && msg.computedResult.value !== null ? (
                    <div className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5">
                      <span className="text-xs font-medium text-blue-600">Result:</span>
                      <span className="text-sm font-semibold text-blue-800">{formatChatMetricValue(msg.computedResult.value)}</span>
                    </div>
                  ) : null}
                </div>
              )}

              {/* Data table for assistant messages (existing drilldown) */}
              {msg.role === 'assistant' && msg.dataResult && msg.dataResult.rows.length > 0 && (
                <div className="max-w-full w-full overflow-x-auto mt-1">
                  <table className="text-xs border-collapse w-full rounded overflow-hidden">
                    <thead>
                      <tr className="bg-gray-200">
                        {msg.dataResult.columns.map((col) => (
                          <th key={col} className="px-2 py-1 text-left font-semibold text-gray-600 whitespace-nowrap">
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {msg.dataResult.rows.map((row, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          {(msg.dataResult?.columns ?? []).map((col) => (
                            <td key={col} className="px-2 py-1 text-gray-700 whitespace-nowrap">
                              {formatChatMetricValue(row[col], col)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

            </div>
          ))}

          {/* Typing indicator */}
          {sendMutation.isPending && (
            <div className="flex items-start gap-2">
              <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-2.5 flex items-center gap-2">
                <Spin size="small" />
                <span className="text-xs text-gray-500">AI is thinking…</span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 bg-gray-50 mb-6">
          {!hasContext && (
            <p className="text-xs text-gray-400 mb-2 text-center">Open a report or group to start chatting.</p>
          )}
          <div className="flex gap-2 items-end">
            <TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question about this report…"
              autoSize={{ minRows: 1, maxRows: 4 }}
              disabled={sendMutation.isPending || !hasContext}
              className="flex-1 text-sm resize-none"
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              disabled={!input.trim() || sendMutation.isPending || !hasContext}
              className="feature-button-solid flex-shrink-0"
            />
          </div>
        </div>
      </div>
    </>
  )
}
